// Deeper analytics (docs/plans/phase3-deeper-analytics.md §4, §5). Filters go in the request
// body, never the address. The CSV is fetched as a file; the server audits it first.
import { queryOptions } from '@tanstack/react-query';
import type { AnalyticsArea } from '@wodi/domain';
import {
  analyticsDrillSchema,
  analyticsSetupSchema,
  analyticsViewSchema,
  type AnalyticsQuery,
} from '@wodi/shared';

import { api, downloadFile } from './client.ts';

export const analyticsKeys = {
  all: ['analytics'] as const,
  setup: ['analytics', 'setup'] as const,
  view: (area: AnalyticsArea, query: AnalyticsQuery) => ['analytics', area, query] as const,
  drill: (area: AnalyticsArea, query: AnalyticsQuery, table: string, rowKey: string) =>
    ['analytics', area, query, 'drill', table, rowKey] as const,
};

export const analyticsSetupQuery = queryOptions({
  queryKey: analyticsKeys.setup,
  queryFn: () => api('GET', '/analytics/setup', { schema: analyticsSetupSchema }),
  staleTime: 60_000,
});

export const fetchAnalytics = (area: AnalyticsArea, body: AnalyticsQuery) =>
  api('POST', `/analytics/${area}`, { body, schema: analyticsViewSchema });

export const fetchDrill = (
  area: AnalyticsArea,
  body: AnalyticsQuery & { table: string; rowKey: string; cursor?: string | undefined },
) => api('POST', `/analytics/${area}/drill`, { body, schema: analyticsDrillSchema });

/** The aggregate as CSV. The file name comes from the server: area, facility code, period. */
export function downloadAnalytics(area: AnalyticsArea, body: AnalyticsQuery): Promise<string> {
  return downloadFile(
    `/api/v1/analytics/${area}/export`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'text/csv' },
      body: JSON.stringify(body),
    },
    `wodi_analytics-${area}.csv`,
  );
}
